import CalendarPopup from '@/components/CalendarPopup/CalendarPopup';
import { ModalCloseIcon } from '@/components/Modal/Modal';
import { useData } from '@/shared/providers/DataProvider';
import * as Sentry from '@sentry/nextjs';
import { getStoryblokApi } from '@storyblok/react';
import { motion } from 'framer-motion';
import { GetServerSidePropsContext } from 'next';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import React from 'react';
import Skeleton from 'react-loading-skeleton';
import styles from './Meet.module.scss';

type CalendarPageProps = {
    calendarId: string;
    slug?: string;
    locale: string;
};
function transformName(str: string) {
    // Split the string by hyphens
    const parts = str.split('-');
    // Capitalize the first letter of each part
    const capitalizedParts = parts.map(part => {
        return part.charAt(0).toUpperCase() + part.slice(1);
    });
    // Join the parts with spaces
    return capitalizedParts.join(' ');
}
const CalendarPage: React.FC<CalendarPageProps> = ({ calendarId, locale, slug }) => {
    const router = useRouter();
    const { data } = useData();

    const url = `https://calendar.google.com/calendar/appointments/schedules/${calendarId}?hl=${locale}&?gv=true&amp`;

    const handleClose = () => {
        data?.lastPageColor ? history.back() : router.push('/');
    };
    const fixSlug = slug && transformName(slug);

    const title = `Meet - ${fixSlug}`;
    const description = locale === 'de' ? `Call vereinbaren mith ${fixSlug}` : `Book a call with ${fixSlug}`;

    return (
        <>
            <NextSeo
                title={title}
                description={description}
                openGraph={{
                    url: `${process.env.NEXT_PUBLIC_BASE_URL}/${router.locale}/meet/${slug}`,
                    title: title,
                    description: description,
                    locale: router.locale,
                    siteName: 'Mindnow',
                }}
                twitter={{
                    handle: '@handle',
                    site: '@site',
                    cardType: 'summary_large_image',
                }}
            />

            <div className={styles.root}>
                <motion.div
                    initial={{
                        opacity: 0,
                        scale: 0.3,
                    }}
                    animate={{
                        width: '100%',
                        opacity: 1,
                        scale: 1,
                        transition: {
                            duration: 0.5,
                            delay: 0.1,
                        },
                    }}
                    exit={{
                        width: '100%',
                        opacity: 0,
                        scale: 0.3,
                    }}
                    className={styles.modal}
                >
                    <div>
                        <ModalCloseIcon onClick={handleClose} />
                    </div>
                    {!url ? <Skeleton height={1200} /> : <CalendarPopup url={url} />}
                </motion.div>
            </div>
        </>
    );
};

export default CalendarPage;

export async function getServerSideProps(context: GetServerSidePropsContext) {
    const production = process.env.NEXT_PUBLIC_ENVIRONMENT === 'production';
    const { slug } = context.query;
    const locale = context?.locale || 'en';
    const cv = Date.now();
    let calendarId = '';

    try {
        const storyblokApi = getStoryblokApi();
        const response = await storyblokApi.get('cdn/stories', {
            version: production ? 'published' : 'draft',
            cv,
            by_slugs: `minds/${slug}`,
        });

        if (response.data && response.data.stories) {
            calendarId = response.data.stories[0].content.calendarId;
        } else {
            throw new Error('Unexpected response format');
        }
    } catch (error) {
        Sentry.captureException('error in fetch mind', {
            tags: {
                component: 'calendar page',
            },
            level: 'error',
        });
    }

    return {
        props: {
            calendarId,
            slug,
            locale,
        },
    };
}
